import {
  Alert,
  AlertIcon,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useId, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  bffApi,
  ContractTemplateDto,
  ViewDocumentAsOwnerDto,
  useCreateContractFromBidMutation,
  useListContractTemplatesQuery,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../common/errors/useApiError";
import { SelectorValue } from "../../../common/input/Selector/SelectorValue";
import { ContractTypeFormSelector } from "../../contracts/ContractTypeFormSelector";
import { ModalSkeleton } from "../../../common/ModalSkeleton";
import { urls } from "../../../urls";
import { useAppDispatch } from "../../../common/redux/hooks";
import { CeilingRefusal, ceilingRefusalOf } from "./ceilingRefusal";

interface Props {
  eventId: string;
  bidId: string;
  supplierName: string;
  defaultTitle: string;
  procurementDocuments: ViewDocumentAsOwnerDto[];
  bidDocuments: ViewDocumentAsOwnerDto[];
  isOpen: boolean;
  onClose: () => void;
}

interface FormValues {
  title: string;
  template: SelectorValue | null;
}

export const CreateContractFromBidModal = ({
  eventId,
  bidId,
  supplierName,
  defaultTitle,
  procurementDocuments,
  bidDocuments,
  isOpen,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const authState = useLoggedInWithOrgContextState();
  const navigate = useNavigate();
  const toast = useToast();
  const apiError = useApiError();

  const tenderOnlyDocuments = useMemo(
    () => bidDocuments.filter((bidDocument) => !procurementDocuments.some((d) => d.id === bidDocument.id)),
    [bidDocuments, procurementDocuments],
  );
  const allDocumentIdsKey = [...procurementDocuments, ...tenderOnlyDocuments].map((d) => d.id).join(",");
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>(() => idsOf(allDocumentIdsKey));
  useEffect(() => {
    if (isOpen) setSelectedDocumentIds(idsOf(allDocumentIdsKey));
  }, [isOpen, allDocumentIdsKey]);
  useEffect(() => {
    if (isOpen) setCeilingRefusal(null);
  }, [isOpen]);

  const toggleDocument = (id: string, checked: boolean) =>
    setSelectedDocumentIds((prev) => (checked ? [...new Set([...prev, id])] : prev.filter((d) => d !== id)));

  const [createContract, { isLoading }] = useCreateContractFromBidMutation();
  const [ceilingRefusal, setCeilingRefusal] = useState<CeilingRefusal | null>(null);
  const dispatch = useAppDispatch();
  const { data: templatesRes, isLoading: isLoadingTemplates } = useListContractTemplatesQuery(
    {
      organizationId: authState.selectedOrg.id,
      orgId: authState.selectedOrg.id,
    },
    { refetchOnFocus: true },
  );

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: { title: defaultTitle, template: null } });

  const getTemplateName = (template: ContractTemplateDto): string => template.name ?? t("Contract");

  const submit = async (values: FormValues) => {
    try {
      const result = await createContract({
        eventId,
        bidId,
        createContractFromBidRequest: {
          title: values.title,
          templateId: values.template?.value ?? undefined,
          documentIds: selectedDocumentIds,
        },
      }).unwrap();
      toast({ title: t("Contract created successfully"), status: "success", position: "top-right" });
      onClose();
      navigate(urls.app.contracts.edit.go(result.id));
    } catch (e) {
      const refusal = ceilingRefusalOf(e);
      if (refusal) {
        setCeilingRefusal(refusal);
        dispatch(bffApi.util.invalidateTags(["FrameworkAgreement"]));
      } else apiError.trigger(e);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="lg">
          {t("New contract with")} {supplierName}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {isLoadingTemplates ? (
            <ModalSkeleton />
          ) : (
            <form id="create-contract-from-bid-form" onSubmit={handleSubmit(submit)}>
              {ceilingRefusal && (
                <Alert status="warning" rounded="md" mb="4" fontSize="sm" alignItems="flex-start">
                  <AlertIcon />
                  {ceilingRefusal === "OVER_CEILING"
                    ? t(
                        "The procurement value would take the framework agreement past its ceiling value. Lower it under «{{field}}» on the award tab, then create the contract.",
                        { field: t("Procurement value") },
                      )
                    : ceilingRefusal === "OTHER_CURRENCY"
                      ? t(
                          "The procurement value is in another currency than the framework agreement's. Save it again under «{{field}}» on the award tab, then create the contract.",
                          { field: t("Procurement value") },
                        )
                      : t(
                          "The procurement has no value, and the framework agreement has a ceiling value. Enter it under «{{field}}» on the award tab, then create the contract.",
                          { field: t("Procurement value") },
                        )}
                </Alert>
              )}
              <FormControl isInvalid={errors.title !== undefined} pt="2.5">
                <FormLabel htmlFor="title">{t("Contract title")}</FormLabel>
                <Input
                  id="title"
                  backgroundColor="smBackground"
                  {...register("title", {
                    required: t("Please provide a title for your contract") ?? "",
                    minLength: { value: 2, message: t("Must be at least 2 characters long") },
                  })}
                />
                <FormErrorMessage>{errors.title && errors.title.message}</FormErrorMessage>
                <FormHelperText>{t("Please provide a title for your contract")}</FormHelperText>
              </FormControl>
              <Flex pt="4">
                {templatesRes && (
                  <ContractTypeFormSelector
                    control={control}
                    name="template"
                    options={templatesRes.templates.map((tpl) => ({ label: getTemplateName(tpl), value: tpl.id }))}
                  />
                )}
              </Flex>
              {allDocumentIdsKey !== "" && (
                <FormControl as="fieldset" pt="4">
                  <FormLabel as="legend">{t("Documents to copy to the contract")}</FormLabel>
                  <Stack spacing="4" maxH="64" overflowY="auto" pr="2">
                    <DocumentGroup
                      heading={t("From the procurement")}
                      documents={procurementDocuments}
                      selectedDocumentIds={selectedDocumentIds}
                      onToggle={toggleDocument}
                    />
                    <DocumentGroup
                      heading={t("From the tender")}
                      documents={tenderOnlyDocuments}
                      selectedDocumentIds={selectedDocumentIds}
                      onToggle={toggleDocument}
                    />
                  </Stack>
                  <FormHelperText>{t("Selected documents are copied onto the new contract.")}</FormHelperText>
                </FormControl>
              )}
              <Flex width="full" justify="end">
                <Button type="submit" form="create-contract-from-bid-form" colorScheme="teal" isLoading={isLoading} mt="8">
                  {t("Create contract")}
                </Button>
              </Flex>
            </form>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const idsOf = (key: string): string[] => (key === "" ? [] : key.split(","));

const DocumentGroup = ({
  heading,
  documents,
  selectedDocumentIds,
  onToggle,
}: {
  heading: string;
  documents: ViewDocumentAsOwnerDto[];
  selectedDocumentIds: string[];
  onToggle: (id: string, checked: boolean) => void;
}) => {
  const headingId = useId();
  if (documents.length === 0) return null;
  return (
    <Stack spacing="2" role="group" aria-labelledby={headingId}>
      <Text id={headingId} color="smText" fontSize="sm" fontWeight="semibold">
        {heading}
      </Text>
      {documents.map((doc) => (
        <Checkbox
          key={doc.id}
          isChecked={selectedDocumentIds.includes(doc.id)}
          onChange={(e) => onToggle(doc.id, e.target.checked)}
        >
          <Text as="span" color="smText" fontSize="sm">
            {doc.name}
          </Text>
        </Checkbox>
      ))}
    </Stack>
  );
};
